import html2pdf from "html2pdf.js";
import { IContext } from "@src/context";
import messages from "./messages";

// ─── Types ──────────────────────────────────────────────────────────

interface Row {
  label: string;
  value: string;
  unit: string;
  indent?: boolean;
}

interface Section {
  title: string;
  rows: Row[];
  subtotal: Row;
}

interface PdfData {
  title: string;
  remark: string;
  sections: Section[];
  total: Row;
  notes: string[];
}

// ─── DOM extraction ─────────────────────────────────────────────────
// Every label, value, unit and note is pulled from the live form DOM
// so translations come for free.

const OPTION_NAMES = [
  "air-conditioner",
  "washbasin-bowl",
  "washbasin-hot-water-supply",
  "kitchen-water-heater",
  "gas-stove",
  "tv-intercom",
  "bidet-heated",
];

const OTHER_CHARGE_NAMES = [
  "catv-usage-fee",
  "water-charges",
  "sewer-charges",
  "utility-fees",
];

const text = (el: Element | null | undefined) => el?.textContent?.trim() ?? "";

const costEl = (container: HTMLElement, name: string) =>
  container.querySelector<HTMLElement>(`[data-initial-cost-name="${name}"]`);

const costValue = (container: HTMLElement, name: string): number =>
  parseInt(costEl(container, name)?.dataset.value ?? "0", 10) || 0;

const costUnit = (container: HTMLElement, name: string): string =>
  text(costEl(container, name)?.parentElement?.querySelector("small"));

const isChecked = (container: HTMLElement, name: string): boolean =>
  container.querySelector<HTMLInputElement>(`input[name="${name}"]`)?.checked ??
  false;

function findLabel(container: HTMLElement, name: string): string {
  // A single input for this name means a standalone field (e.g. a
  // checkbox option) — its sibling label is what we want. Multiple
  // inputs sharing a name is a radio group whose per-option labels
  // aren't the field name; skip this path in that case.
  const inputs = container.querySelectorAll<HTMLInputElement>(
    `input[name="${name}"]`
  );
  if (inputs.length === 1) {
    const inputLabel = inputs[0]
      .closest(".container-initial-cost-field")
      ?.querySelector(".container-initial-cost-label");
    if (inputLabel) return text(inputLabel);
  }

  const el = costEl(container, name);
  if (!el) return "";

  // Deliberately *not* matching `.container-initial-cost-list-item-end`:
  // fire-insurance's amount span lives in a list-item-end alongside the
  // radio-option labels, so matching there would return the first
  // option label instead of the ⑭ field heading.
  const row = el.closest(".container-initial-cost-list-item-row");
  const rowLabel = row?.querySelector(
    ".container-initial-cost-list-item-name, .container-initial-cost-label"
  );
  if (rowLabel) return text(rowLabel);

  const scope = el.closest(
    ".container-initial-cost-subtotal, .container-initial-cost-total, .container-initial-cost-list-item"
  );
  return text(scope?.querySelector(".container-initial-cost-list-item-name"));
}

function makeRow(
  container: HTMLElement,
  name: string,
  overrides: Partial<Row> = {}
): Row {
  const el = costEl(container, name);
  return {
    label: findLabel(container, name),
    value: text(el) || "0",
    unit: costUnit(container, name),
    ...overrides,
  };
}

const makeSubRow = (container: HTMLElement, name: string): Row =>
  makeRow(container, name, { indent: true });

// ④ その他賃料 has no amount element of its own — the amount is the sum
// of its sub-items. Fetch the heading label from the enclosing list
// item, borrow the unit from the first sub-item, and format the sum
// via the browser's default locale.
function makeGroupedRow(
  container: HTMLElement,
  names: string[],
  firstChildName: string
): Row {
  const parent = costEl(container, firstChildName)?.closest(
    ".container-initial-cost-list-item"
  );
  const sum = names.reduce((s, n) => s + costValue(container, n), 0);
  return {
    label: text(
      parent?.querySelector(".container-initial-cost-list-item-name")
    ),
    value: sum.toLocaleString(),
    unit: costUnit(container, firstChildName),
  };
}

// Sibling label of the checked radio, e.g. "1台" for air-conditioner-count.
const checkedRadioLabel = (container: HTMLElement, name: string): string =>
  text(
    container.querySelector(
      `input[name="${name}"]:checked ~ .container-initial-cost-label`
    )
  );

function collectBasicRows(container: HTMLElement): Row[] {
  const rows: Row[] = [
    makeRow(container, "monthly-rent"),
    makeRow(container, "maintenance-fee"),
    // ③ heading — amount hidden, shown per sub-item below.
    makeRow(container, "additional-options", { value: "", unit: "" }),
  ];

  // ③ sub-items: each selected option with a non-zero value
  for (const name of OPTION_NAMES) {
    if (isChecked(container, name) && costValue(container, name) !== 0) {
      const sub = makeSubRow(container, name);
      if (name === "air-conditioner") {
        const count = checkedRadioLabel(container, "air-conditioner-count");
        if (count) sub.label = `${sub.label}   ${count}`;
      }
      rows.push(sub);
    }
  }

  // ④ always shown; sub-items appear only when non-zero
  rows.push(makeGroupedRow(container, OTHER_CHARGE_NAMES, "catv-usage-fee"));
  for (const name of OTHER_CHARGE_NAMES) {
    if (costValue(container, name) !== 0) {
      rows.push(makeSubRow(container, name));
    }
  }

  rows.push(makeRow(container, "parking-fee"));
  return rows;
}

function collectVariableRows(container: HTMLElement): Row[] {
  const dateInput = container.querySelector<HTMLInputElement>(
    'input[name="move-in-date"]'
  );
  const moveInLabel = text(
    dateInput
      ?.closest(".container-initial-cost-list-item-row")
      ?.querySelector(".container-initial-cost-list-item-name")
  );
  return [
    { label: moveInLabel, value: dateInput?.value ?? "", unit: "" },
    makeRow(container, "prorated-rent"),
    makeRow(container, "prorated-maintenance-fee"),
    makeRow(container, "other-rental-charges"),
    makeRow(container, "prorated-parking-fee"),
  ];
}

const collectOtherRows = (container: HTMLElement): Row[] => [
  makeRow(container, "deposit-amount"),
  makeRow(container, "fire-insurance"),
  makeRow(container, "moving-support"),
];

function collectData(context: IContext, container: HTMLElement): PdfData {
  const title = text(
    container.querySelector(".container-initial-cost-heading-title")
  );
  const date =
    container.querySelector<HTMLInputElement>('input[name="move-in-date"]')
      ?.value ?? "";
  const remark = date
    ? context.intl.formatMessage(messages.PDFMoveInDate, { date })
    : "";
  const sectionTitles = Array.from(
    container.querySelectorAll(".container-initial-cost-section-title")
  ).map(text);

  const sections: Section[] = [
    {
      title: sectionTitles[0] ?? "",
      rows: collectBasicRows(container),
      subtotal: makeRow(container, "basic-initial-cost-subtotal"),
    },
    {
      title: sectionTitles[1] ?? "",
      rows: collectVariableRows(container),
      subtotal: makeRow(container, "variable-initial-cost-subtotal"),
    },
    {
      title: sectionTitles[2] ?? "",
      rows: collectOtherRows(container),
      subtotal: makeRow(container, "other-fees-subtotal"),
    },
  ];

  const notes = Array.from(
    container.querySelectorAll(".container-initial-cost-footer-annotate p")
  )
    .map(text)
    .filter(Boolean);

  return {
    title,
    remark,
    sections,
    total: makeRow(container, "initial-costs-total"),
    notes,
  };
}

// ─── Offscreen HTML ─────────────────────────────────────────────────
// Every element has `color:#000` inline so the site's global stylesheet
// can't bleed its palette into the PDF. Rows alternate with a grey
// background; sub-items count as their own row in the alternation.
// Subtotals and total stand out purely through weight and size.

const SHADE_BG = "#f5f5f5";
const ROOT_STYLE =
  "box-sizing:border-box;width:180mm;color:#000;font-family:inherit;";
const H1_STYLE =
  "margin:0;text-align:center;font-size:20px;font-weight:700;color:#000;";
const H2_STYLE = "margin:0 0 16px;font-size:16px;font-weight:700;color:#000;";
const REMARK_STYLE =
  "margin:4px 0 0;text-align:right;font-size:14px;color:#000;";
const NOTE_STYLE = "margin:4px 0;font-size:13px;line-height:1.5;color:#000;";
const SECTION_STYLE = "margin:12px 0 0;color:#000;";
const ROW_STYLE =
  "display:flex;justify-content:space-between;align-items:center;line-height:1.3;color:#000;";
const SUMMARY_STYLE =
  "display:flex;justify-content:flex-end;align-items:center;gap:32px;padding:6px 12px;line-height:1.5;color:#000;";

const formatAmount = ({ value, unit }: Row) =>
  !value ? "" : unit ? `${value} ${unit}` : value;

function escapeHtml(s: string): string {
  const map: Record<string, string> = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };
  return s.replace(/[&<>"']/g, (c) => map[c]);
}

const shadeRows = (rows: Row[]): Array<Row & { shaded: boolean }> =>
  rows.map((row, i) => ({ ...row, shaded: i % 2 === 0 }));

function rowHtml(row: Row & { shaded: boolean }): string {
  const bg = row.shaded ? `background:${SHADE_BG};` : "";
  const padLeft = row.indent ? 36 : 12;
  const label = row.indent ? `・${row.label}` : row.label;
  return `
    <div style="${ROW_STYLE}padding:0 12px 12px ${padLeft}px;${bg}font-size:13px;">
      <span>${escapeHtml(label)}</span>
      <span style="white-space:nowrap;">${escapeHtml(formatAmount(row))}</span>
    </div>`;
}

function summaryHtml(row: Row, fontSize: number): string {
  return `
    <div style="${SUMMARY_STYLE}font-weight:700;font-size:${fontSize}px;">
      <span>${escapeHtml(row.label)}</span>
      <span style="white-space:nowrap;">${escapeHtml(formatAmount(row))}</span>
    </div>`;
}

function sectionHtml(section: Section): string {
  return `
    <section style="${SECTION_STYLE}">
      <h2 style="${H2_STYLE}">${escapeHtml(section.title)}</h2>
      ${shadeRows(section.rows).map(rowHtml).join("")}
      ${summaryHtml(section.subtotal, 14)}
    </section>`;
}

function buildElement(data: PdfData): HTMLElement {
  const remark = data.remark
    ? `<p style="${REMARK_STYLE}">${escapeHtml(data.remark)}</p>`
    : "";
  const notes = data.notes
    .map((n) => `<p style="${NOTE_STYLE}">${escapeHtml(n)}</p>`)
    .join("");

  const root = document.createElement("div");
  root.style.cssText = ROOT_STYLE;
  root.innerHTML = `
    <h1 style="${H1_STYLE}">${escapeHtml(data.title)}</h1>
    ${remark}
    ${data.sections.map(sectionHtml).join("")}
    <div style="margin-top:4px;">${summaryHtml(data.total, 16)}</div>
    <div style="margin-top:16px;color:#000;">${notes}</div>`;
  return root;
}

// ─── Entry point ────────────────────────────────────────────────────

export async function generatePdf(context: IContext, container: HTMLElement) {
  const data = collectData(context, container);
  const element = buildElement(data);

  await html2pdf()
    .set({
      margin: 15,
      filename: `${context.intl.formatMessage(messages.PDFFileName)}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff" },
      jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
    })
    .from(element)
    .save();
}
